import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Params } from '@angular/router';
import { apis } from '../config/api';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BookingsService } from './booking.service';



@Injectable({
  providedIn: 'root'
})

export class StudentsService {
  http = inject(HttpClient);
  private bookingsSvc = inject(BookingsService);

  url = apis.students;
  listSchoolStudents(params: Params, schoolId: string): Observable<any> {
    const httpParams = new HttpParams({ fromObject: params });
    return this.http.get(`${this.url}/${schoolId}`, { params: httpParams });
  }

  addSchoolStudent(body: Params, schoolId: string): Observable<any> {
    return this.http.post(`${this.url}/${schoolId}`, body);
  }

  updateSchoolStudent(params: Params, schoolId: string, studentId: string): Observable<any> {
    return this.http.patch(`${this.url}/${schoolId}/${studentId}`, params);
  }

  deleteSchoolStudent(schoolId: string, id: string): Observable<any> {
    return this.http.delete(`${this.url}/${schoolId}/${id}`);
  }

  getDependents(schoolId: string, guardianUserId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.url}/${schoolId}/guardian/${guardianUserId}/dependents`);
  }

  listStudentsByBooking(params: Params, schoolId: string): Observable<any> {
    const httpParams = new HttpParams({ fromObject: params });
    return this.http.get(`${this.url}/${schoolId}/by-booking`, { params: httpParams });
  }

  listEnrollmentProducts(studentId: string) {
    return this.http.get(`${this.url}/${studentId}/enrollment-products`)
  }

  getBalanceBreakdown(schoolId: string, studentId: string): Observable<any> {
    return this.http.get(`${this.url}/${schoolId}/${studentId}/balance-breakdown`);
  }

  /** Where the family's money sits and how much of each can leave (held / in favour). */
  getMoneySources(schoolId: string, studentId: string, currency?: string): Observable<any> {
    return this.http.get(`${this.url}/${schoolId}/${studentId}/refundable`, { params: currency ? { currency } : {} });
  }

  updateBookingStatus(bookingId: string, schoolId: string, status: number): Observable<any> {
    return this.bookingsSvc.updateBooking(bookingId, schoolId, { status });
  }

  deleteBooking(bookingId: string, schoolId: string): Observable<any> {
    return this.bookingsSvc.deleteBooking(bookingId, schoolId);
  }

  recordPayment(
    schoolId: string,
    studentId: string,
    body: {
      // Required only when cash actually moves (amount or advance > 0) —
      // a discount-only submit has nothing to record a payment method
      // for. The server normalizes/validates this; null is always safe
      // to send for a pure discount.
      paymentMethodId: string | null;
      currency: string;
      note?: string | null;
      // Untargeted advance — cash recorded on the same receipt that
      // didn't fit on any item. Becomes a wallet credit and is consumed
      // chronologically by future charges.
      advance?: number;
      // Which charges that wallet money may pay later: 1 any, 2 lessons,
      // 3 lockers, 4 rentals. Defaults to any.
      advanceScope?: number;
      // false: wallet money stays in favour instead of paying charges
      // already posted and unpaid right away. Omitted: as before.
      applyAdvanceNow?: boolean;
      // Money in favour on other items, spent on the allocations' `credit`.
      credits?: Array<{ bookingId?: string; boxBookingId?: string; rentalId?: string; amount: number }>;
      allocations: Array<{
        bookingId?: string;
        boxBookingId?: string;
        rentalId?: string;
        amount?: number;
        // Paid from `credits` (money in favour on other items), not cash.
        credit?: number;
        discount?: number;
        // Percentage alternative to `discount` — the server resolves it
        // against the item's outstanding (or its remaining price when
        // nothing has been charged yet).
        discountPercent?: number;
        // Promo code redeemed against this item. Stacks with `discount`.
        discountCode?: string;
      }>;
      // Makes a retried or double-clicked submit replay the original
      // receipt instead of recording a second payment.
      idempotencyKey?: string;
    },
  ): Observable<any> {
    return this.http.post(`${this.url}/${schoolId}/${studentId}/payment`, body);
  }
}